import { useEffect, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { hapticDone } from '../engine/haptics.ts';
import { reducedMotion } from '../engine/haptics.ts';
import { CREAM, INK, LINE, MUTED, ORANGE, SHADOW_CARD } from '../ui/theme.ts';

/**
 * score-ring: the count of a multi-select answer (`from`) out of `max`, as a
 * percentage drawn 0 → pct over 900 ms. `{pct}` in the copy is the number.
 * Answer: { value: pct, label: "pct%" }.
 */
export function ScoreRing({ step, content, ctx }: StepProps) {
  const key = step.answerKey ?? 'score';
  const source = ctx.answers[step.from ?? ''] as { count?: number; values?: unknown[] } | undefined;
  const count = source?.count ?? source?.values?.length ?? 0;
  const max = step.max ?? 4;
  const pct = Math.round((Math.min(count, max) / max) * 100);
  const [shown, setShown] = useState(reducedMotion() ? pct : 0);

  useEffect(() => {
    ctx.setAnswer(key, { value: pct, label: `${pct}%` });
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', large: true, onPress: () => ctx.next() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id, pct, content.cta]);

  useEffect(() => {
    if (reducedMotion()) return;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(pct * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
      else hapticDone();
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [pct]);

  const r = 62;
  const c = 2 * Math.PI * r;
  const fill = (text?: string) => (text ?? '').replace(/\{pct\}/g, String(shown));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--fk-title-top)' }}>
        <div style={{ position: 'relative', width: 168, height: 168 }} role="img" aria-label={`${pct}%`}>
          <svg viewBox="0 0 168 168" width="168" height="168" style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
            <circle cx="84" cy="84" r={r} fill="none" stroke={LINE} strokeWidth="14" />
            <circle
              cx="84"
              cy="84"
              r={r}
              fill="none"
              stroke={ORANGE}
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={c - (c * shown) / 100}
            />
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, fontWeight: 800, letterSpacing: '-.03em', color: INK }}>
            {shown}%
          </div>
        </div>
      </div>

      <TitleBlock label={content.label} title={fill(content.title)} highlight={content.highlight} subtitle={fill(content.subtitle)} top={0} />

      {content.paragraphs?.length ? (
        <div style={{ padding: 14, borderRadius: 18, background: CREAM, border: `1.5px solid ${LINE}`, boxShadow: SHADOW_CARD, color: MUTED, fontSize: 14, fontWeight: 600, textAlign: 'center' }}>
          {content.paragraphs.map((p) => (
            <p key={p} style={{ margin: 0 }}>
              {fill(p)}
            </p>
          ))}
        </div>
      ) : null}
    </>
  );
}
